import { ArrowRight, ArrowUpRight, Boxes, Castle, Check, CircleDot, FlipHorizontal2, Grid3x3, House, LayoutGrid, LayoutTemplate, LoaderCircle, type LucideIcon, Minus, Plus, RotateCw, Trash2, Wheat, X } from "lucide-react";
import { useMemo, useState } from "react";
import { PRESETS, type Template } from "@mcp/plan";
import { controller } from "../app/controller.ts";
import { useApp } from "../state/store.ts";
import "./layouts.css";

const ICON: Record<string, LucideIcon> = { grid: Grid3x3, village: House, radial: CircleDot, fields: Wheat, walls: Castle };

function PresetIcon({ id, size = 14 }: { id: string; size?: number }) {
  const I = ICON[id];
  return I ? <I size={size} aria-hidden="true" /> : null;
}

/**
 * "Layouts & templates": a glass panel inside the map card, next to the tool dock (key 6). Fill an area / path with a
 * preset, place or save templates. Schematics live in their own full window (ui/schematics/, key 7); while one is
 * being placed on the map, its controls show here instead.
 */
export function LayoutPanel() {
  const tab = useApp((s) => s.layoutPanel);
  const placing = useApp((s) => s.schemPlace);
  if (placing) return <SchematicPlacing />;
  if (!tab || tab === "schematics") return null;
  return (
    <aside className="layout-panel lp glass-2" aria-label="Layouts and templates">
      <div className="lp-head">
        <div className="seg lp-tabs" role="tablist" aria-label="Layouts and templates">
          <button type="button" role="tab" aria-selected={tab === "fill"} aria-pressed={tab === "fill"} className={tab === "fill" ? "on" : ""} onClick={() => controller.openLayouts("fill")}>
            <LayoutGrid size={14} aria-hidden="true" />Fill area
          </button>
          <button type="button" role="tab" aria-selected={tab === "templates"} aria-pressed={tab === "templates"} className={tab === "templates" ? "on" : ""} onClick={() => { controller.cancelLayout(); controller.openLayouts("templates"); }}>
            <LayoutTemplate size={14} aria-hidden="true" />Templates
          </button>
        </div>
        <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => controller.openLayouts(null)} title="Close · 6" aria-label="Close"><X size={16} aria-hidden="true" /></button>
      </div>
      <div className="lp-body">{tab === "fill" ? <FillTab /> : <TemplatesTab />}</div>
      <div className="lp-foot">
        <button type="button" className="lp-schem-link" onClick={() => controller.openSchematics()} title="Schematics · 7">
          <span className="lp-schem-icon"><Boxes size={16} aria-hidden="true" /></span>
          <span className="grow"><b>Schematics</b><span className="caption">Place real builds from .litematic, .schem and .nbt files</span></span>
          <kbd>7</kbd>
        </button>
      </div>
    </aside>
  );
}

function FillTab() {
  const layout = useApp((s) => s.layout);
  const sel = useApp((s) => s.plans.find((p) => p.id === s.selectedId));
  const plans = useApp((s) => s.plans);
  const target = layout?.mode === "fill" ? plans.find((p) => p.id === layout.targetId) : null;
  const summary = useMemo(() => (layout?.mode === "fill" ? controller.layoutSummary(controller.fillObjects(layout)) : null), [layout, plans]);

  if (!layout || layout.mode !== "fill" || !target) {
    const ok = sel && (sel.kind === "area" || sel.kind === "path");
    return (
      <div className="fill-tab lp-intro">
        <p className="lp-lead">Fill an <b>area</b> with a town layout, or build a <b>village along a path</b>. You see a preview on the map before anything is made.</p>
        {ok
          ? <button type="button" className="btn btn-md btn-primary btn-block" onClick={() => controller.startFill(sel.id)}>Fill {sel.name}</button>
          : <p className="lp-hint">Draw or select an area <kbd>3</kbd> or a path <kbd>2</kbd> first, then come back here · or use “Fill with a layout…” in its panel.</p>}
        <div className="overline lp-cap">Presets</div>
        <ul className="preset-list">
          {PRESETS.map((p) => (
            <li key={p.id}>
              <span className="lp-preset-icon"><PresetIcon id={p.id} size={16} /></span>
              <span className="grow"><b>{p.name}</b><span className="caption">{p.description}</span></span>
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const presets = PRESETS.filter((p) => p.on === (target.kind === "path" ? "path" : "area"));
  const preset = PRESETS.find((p) => p.id === layout.preset)!;
  const shown = preset.params.filter((p) => (!p.show || p.show.values.includes(layout.params[p.show.key] ?? 0)) && !(p.hide && p.hide.values.includes(layout.params[p.hide.key] ?? 0)));
  return (
    <div className="fill-tab">
      <div className="lp-target"><span className="caption">Filling</span><b title={target.name}>{target.name}</b></div>
      <div className="preset-grid" role="radiogroup" aria-label="Preset">
        {presets.map((p) => (
          <button key={p.id} type="button" role="radio" aria-checked={p.id === layout.preset} className={p.id === layout.preset ? "on" : ""} onClick={() => controller.updateLayout({ preset: p.id })} title={p.description}>
            <span className="preset-icon"><PresetIcon id={p.id} size={20} /></span>{p.name}
          </button>
        ))}
      </div>
      <label className="param scale">
        <span>Size <span className="caption">hamlet to city</span></span>
        <b className="tabular">×{layout.scale.toFixed(2)}</b>
        <input type="range" min={0.5} max={2} step={0.05} value={layout.scale} onChange={(e) => controller.updateLayout({ scale: +e.target.value })} />
      </label>
      {shown.map((p) => (
        p.options
          ? (
            <label key={p.key} className="param select">
              <span>{p.label}</span>
              <select value={layout.params[p.key] ?? p.def} onChange={(e) => controller.updateLayout({ params: { ...layout.params, [p.key]: +e.target.value } })}>
                {p.options.map((o, i) => <option key={i} value={i}>{o}</option>)}
              </select>
            </label>
          )
          : p.max === 1 && p.min === 0 && p.step === 1
          ? <label key={p.key} className="check lp-check"><input type="checkbox" checked={!!layout.params[p.key]} onChange={(e) => controller.updateLayout({ params: { ...layout.params, [p.key]: e.target.checked ? 1 : 0 } })} /><span>{p.label}</span></label>
          : (
            <label key={p.key} className="param">
              <span>{p.label}</span>
              <b className="tabular">{layout.params[p.key] ?? p.def}{p.unit === "°" ? "°" : ""}{p.scales && layout.scale !== 1 ? <span className="lp-becomes"> <ArrowRight size="1em" aria-label="becomes" /> {Math.max(1, Math.round((layout.params[p.key] ?? p.def) * layout.scale))}</span> : null}</b>
              <input type="range" min={p.min} max={p.max} step={p.step} value={layout.params[p.key] ?? p.def} onChange={(e) => controller.updateLayout({ params: { ...layout.params, [p.key]: +e.target.value } })} />
            </label>
          )
      ))}
      {summary && (
        <p className="measure">
          {[
            summary.houses ? `${summary.houses} houses` : "",
            summary.areas ? `${summary.areas} ${layout.preset === "fields" ? "field" : "area"}${summary.areas > 1 ? "s" : ""}${layout.preset === "fields" ? ` (${summary.areaBlocks.toLocaleString("en-US")} blocks)` : ""}` : "",
            summary.water ? `${summary.water.toLocaleString("en-US")} blocks of water` : "",
            summary.streets ? `${summary.streets.toLocaleString("en-US")} blocks of ${layout.preset === "fields" ? "path" : "street"} (${summary.pave.toLocaleString("en-US")} to pave)` : "",
          ].filter(Boolean).join(" · ") || "Nothing fits · try smaller sizes"}
        </p>
      )}
      <div className="lp-actions">
        <button type="button" className="btn btn-md btn-primary lp-create" disabled={!summary?.total} onClick={() => controller.applyLayout()}>Create {summary?.total ?? 0} objects</button>
        <button type="button" className="btn btn-md btn-outline" onClick={() => controller.openLayouts(null)}>Cancel</button>
      </div>
      <p className="lp-hint">The preview is on the map. Everything is made as one group · <kbd>Ctrl</kbd> <kbd>Z</kbd> removes it again.</p>
    </div>
  );
}

function TemplatesTab() {
  const layout = useApp((s) => s.layout);
  const worldT = useApp((s) => s.worldTemplates);
  const mineT = useApp((s) => s.myTemplates);
  const selCount = useApp((s) => s.selectedIds.length);
  const [name, setName] = useState("");
  const [mine, setMine] = useState(true);
  const [saved, setSaved] = useState<string | null>(null);
  const placing = layout?.mode === "place" ? layout : null;
  const all = controller.allTemplates();

  const row = (t: Template, where: "builtin" | "world" | "mine") => (
    <div key={where + t.id} className={`tpl-item${placing?.templateId === t.id ? " on" : ""}`}>
      <span className="lp-tpl-icon"><LayoutTemplate size={14} aria-hidden="true" /></span>
      <div className="grow">
        <b>{t.name}</b>
        <span className="caption">{t.objects.length} objects{t.author ? ` · ${t.author}` : ""}</span>
      </div>
      <button type="button" className="btn btn-sm btn-outline lp-place" onClick={() => controller.startPlacing(t.id)}>Place</button>
      {where !== "builtin" && <button type="button" className="btn btn-sm btn-ghost btn-icon lp-del" onClick={() => controller.deleteTemplate(t.id)} title="Delete template" aria-label="Delete template"><Trash2 size={14} aria-hidden="true" /></button>}
    </div>
  );

  return (
    <div className="tpl-tab">
      {placing && (
        <div className="placing lp-placing-box" role="status">
          <b>Click on the map to place it</b>
          <span className="caption">Shift+click places more</span>
          <div className="lp-tools">
            <button type="button" className="btn btn-sm btn-outline" onClick={() => controller.updateLayout({ turns: (placing.turns + 1) % 4 })} title="Rotate · R"><RotateCw size={14} aria-hidden="true" />Rotate<kbd>R</kbd></button>
            <button type="button" className="btn btn-sm btn-outline" aria-pressed={placing.mirror} onClick={() => controller.updateLayout({ mirror: !placing.mirror })} title="Mirror · F"><FlipHorizontal2 size={14} aria-hidden="true" />Mirror<kbd>F</kbd></button>
            <span className="lp-scale" role="group" aria-label="Scale">
              <button type="button" className="btn btn-sm btn-icon" onClick={() => controller.updateLayout({ scale: Math.max(0.25, placing.scale - 0.25) })} title="Smaller · [" aria-label="Smaller"><Minus size={14} aria-hidden="true" /></button>
              <span className="tabular">×{placing.scale}</span>
              <button type="button" className="btn btn-sm btn-icon" onClick={() => controller.updateLayout({ scale: Math.min(4, placing.scale + 0.25) })} title="Bigger · ]" aria-label="Bigger"><Plus size={14} aria-hidden="true" /></button>
            </span>
          </div>
          <label className="check lp-check"><input type="checkbox" checked={placing.scaleSizes} onChange={(e) => controller.updateLayout({ scaleSizes: e.target.checked })} /><span>Also scale path widths and house heights</span></label>
          <button type="button" className="btn btn-sm btn-ghost lp-stop" onClick={() => controller.cancelLayout()}>Stop placing<kbd>Esc</kbd></button>
        </div>
      )}
      <div className="tpl-group"><div className="overline">Starter templates</div>{all.builtin.map((t) => row(t, "builtin"))}</div>
      <div className="tpl-group"><div className="overline">This world · shared</div>{worldT.length ? worldT.map((t) => row(t, "world")) : <p className="caption lp-none">None yet</p>}</div>
      <div className="tpl-group"><div className="overline">My templates · all worlds, this browser</div>{mineT.length ? mineT.map((t) => row(t, "mine")) : <p className="caption lp-none">None yet</p>}</div>
      <div className="share-box lp-save">
        <b>Save the selection as a template</b>
        {selCount ? (
          <form onSubmit={(e) => { e.preventDefault(); const t = controller.saveTemplate(name, mine); if (t) { setSaved(t.name); setName(""); } }}>
            <label className="field-box"><input placeholder={`Name · ${selCount} object${selCount > 1 ? "s" : ""}`} value={name} onChange={(e) => setName(e.target.value)} aria-label="Template name" /></label>
            <label className="check lp-check"><input type="checkbox" checked={mine} onChange={(e) => setMine(e.target.checked)} /><span>Also keep it in my templates for other worlds</span></label>
            <button type="submit" className="btn btn-md btn-primary">Save template</button>
          </form>
        ) : <p className="caption">Select objects on the map (Shift+click for several), then save them here.</p>}
        {saved && <p className="ok lp-saved"><Check size={14} aria-hidden="true" />Saved {saved}</p>}
      </div>
    </div>
  );
}

/** While a schematic follows the mouse on the map: what it is, Turn / Mirror / Stop, and the way back. */
function SchematicPlacing() {
  const placing = useApp((s) => s.schemPlace)!;
  const e = useApp((s) => s.schematics.find((x) => x.hash === s.schemPlace?.hash));
  const meshing = useApp((s) => s.schemMeshing);
  if (!e) return null;
  const odd = placing.turns & 1;
  const img = e.thumb ?? e.preview;
  return (
    <aside className="schem-placing glass-2" aria-label={`Placing ${e.name}`} role="status">
      <div className="sp-head">
        <span className={`sp-thumb${img ? "" : " none"}`}>{img ? <img src={img} alt="" /> : <Boxes size={18} aria-hidden="true" />}<span className="sp-ring" aria-hidden="true" /></span>
        <span className="grow">
          <span className="overline">Placing</span>
          <b title={e.name}>{e.name}</b>
          <span className="caption mono">{odd ? e.size[2] : e.size[0]} × {odd ? e.size[0] : e.size[2]} · {e.size[1]} high</span>
        </span>
        <button type="button" className="btn btn-ghost btn-icon btn-sm" onClick={() => controller.cancelSchematicPlacing()} title="Stop · Esc" aria-label="Stop placing"><X size={16} aria-hidden="true" /></button>
      </div>
      <p className="sp-hint">Click on the map to place it · Shift+click places more</p>
      <div className="sp-tools">
        <button type="button" className="btn btn-sm btn-outline" onClick={() => controller.updateSchematicPlacing({ turns: (placing.turns + 1) % 4 })} title="Turn · R"><RotateCw size={14} aria-hidden="true" />Turn<kbd>R</kbd></button>
        <button type="button" className="btn btn-sm btn-outline" aria-pressed={placing.mirror} onClick={() => controller.updateSchematicPlacing({ mirror: !placing.mirror })} title="Mirror · F"><FlipHorizontal2 size={14} aria-hidden="true" />Mirror<kbd>F</kbd></button>
        <span className="grow" />
        <button type="button" className="btn btn-sm btn-ghost" onClick={() => controller.openSchematics(e.hash)} title="Back to schematics · 7">Schematics<ArrowUpRight size={14} aria-hidden="true" /></button>
      </div>
      {meshing && <span className="sp-busy caption"><LoaderCircle className="spin" size={14} aria-hidden="true" />Building the 3D preview…</span>}
    </aside>
  );
}
